import { pushSyncBooking, putSynchedBooking } from "@/api/booking/booking";
import {
  IProductSlotBooking,
  IUntypedBooking,
} from "@/types/db/product-slot-booking";
import { SharedCallbacks } from "@/utils/shared-resources";
import { cloneDeep } from "lodash";
import storage, { LocalStorageKeys } from "@/services/storage.service";
import {
  ISyncField,
  ITimeSlotSyncCondition,
  IRetailSyncCondition,
  ISyncUpdates,
} from "@/types/db/timeslots";
import { getSyncedTimeslots } from "@/api/gql/timeslots";
import { getTimeslotsByIds } from "@/dexie/timeslots";
import { updateTimeslots } from "@/utils/helpers/timeslots";
import { getSyncedPrices } from "@/api/gql/product-slot-prices";
import { updatePrices } from "@/utils/helpers/product-category";
import {
  getProductCategoriesByPriceIds,
  getProductCategoriesByIds,
  getProductCategoriesByStockIds,
} from "@/dexie/product-category";
import { LogLevel, LogService } from "@/services/logging";
import { setCustomerInfo } from "@/utils/helpers/product-slot-booking";
import { PaymentMethods } from "@/types/db/accounting";
import { PSSType } from "@/types/db/product-slot-state";
import BookingModule from "@/store/modules/booking";
import { ISynchingReason, SyncType } from "@/interfaces/store/IBooking";
import { checkAndSynchBookingsFiles } from "@/utils/vue-helpers/pos-files";
import POSConfigModule from "@/store/modules/pos-config";
import AppModule from "@/store/modules/app";
import dayjs from "dayjs";
import { getBookingsByCashfloatUuid } from "./untyped-booking";
import { isBookingInDexie } from "@/utils/helpers/booking";

export async function syncAvailabilities(bookings: IProductSlotBooking[]) {
  try {
    // 1- sync timeslots
    // 2- Update timeslots checkpoint and local db
    await proceedTimeslotsSyncing(bookings);
    // 3- sync retail
    // 4- Update retail checkpoint and local db
    await proceedRetailsSyncing(bookings);
  } catch (error) {
    LogService.info(
      `Availability: Error when synching availabilities for bookings: ${bookings
        .map((b) => b.uuid)
        .join(",")}`,
      { error },
      LogLevel.Error
    );
  }
}

/**
 * 1- Pull concerned timeslots from remote
 * 2- Update the timeslot checkpoint value
 * 3- Update the timeslots values in indexedDB
 * @param bookings
 */
export async function proceedTimeslotsSyncing(bookings: IProductSlotBooking[]) {
  LogService.info(
    `Synching timeslots availabilities, bookings: ${bookings
      .map((b) => b.uuid)
      .join(" ")}`
  );

  // lets build categories Ids that are concerned by the synced bookings
  const pssWithTimeSlots = bookings
    .map((b) => b.pssList.filter((pss) => pss.timeSlotId))
    .flat();
  const categoryIds = [
    ...new Set(pssWithTimeSlots.map((pss) => pss.categoryId)),
  ];

  if (!categoryIds || !categoryIds.length) {
    // The booking had only retail products, lets  not try to sync timeslots
    return;
  }

  // Synch not only categories of the booking but categories linked to the same stock entity as well
  // 1- get the categories with categoryIds from localDB
  const dbCategories = await getProductCategoriesByIds(
    categoryIds.map((c) => Number(c))
  );

  const categoryIdsToSynch: number[] = [];
  // 2- consider their stocksIds array
  const stockIds: number[] = [];
  dbCategories.forEach((c) => {
    if (c.stockIds && c.stockIds.length) {
      c.stockIds.forEach((s) => {
        if (s) {
          stockIds.push(Number(s));
        }
      });
    } else {
      // not a shared stock ticket, add its category id directly to be synced
      categoryIdsToSynch.push(c.id);
    }
  });
  // 3- use stocksIds array to get a list of categories from localDB to synch
  const categoriesToSynch = await getProductCategoriesByStockIds(stockIds);

  // add shared stocks category ids to be synced
  categoryIdsToSynch.push(...categoriesToSynch.map((c) => c.id));

  // get all unique time ranges of the pss list
  const timeRnagesToSynch = new Set<string>();
  for (const pss of pssWithTimeSlots) {
    if (!pss.startDateTime || !pss.endDateTime) {
      continue;
    }
    timeRnagesToSynch.add(
      `(startDateTime,le,${dayjs(pss.endDateTime)
        .utc()
        .format("YYYY-MM-DD HH:mm:ss")})~and(endDateTime,ge,${dayjs(
        pss.startDateTime
      )
        .utc()
        .format("YYYY-MM-DD HH:mm:ss")})`
    );
  }

  const cond: ITimeSlotSyncCondition = {
    ids: categoryIdsToSynch,
    timeRanges: Array.from(timeRnagesToSynch),
  };

  // 1- Pull concerned timeslots from remote db
  const updatedTimeslots = await getSyncedTimeslots(cond);

  // 3- Update the timeslots values in indexedDB if updatedTimeslots has elements inside
  if (!updatedTimeslots || !updatedTimeslots.length) return;

  // Lets build a typed array of the updates we need to perform in the local db
  const updates: ISyncUpdates[] = [];

  for (const ts of updatedTimeslots) {
    const tsFields: ISyncField[] = [];

    for (const prop in ts) {
      if (prop === "id" || prop === "categoryId" || prop === "__typename") {
        // we do not updated the id and categoryId of a timeslot
        continue;
      }
      tsFields.push({ key: prop, value: String(ts[prop]) });
    }

    const upd: ISyncUpdates = { id: String(ts?.id), fields: tsFields };
    updates.push(upd);
  }

  await updateTimeslots(updates);
}

/**
 * 1- Pull concerned prices from remote
 * 2- Update the retail checkpoint value
 * 3- Update the prices values in indexedDB
 * @param bookings
 */
export async function proceedRetailsSyncing(bookings: IProductSlotBooking[]) {
  LogService.info(
    `Synching retails availabilities, bookings: ${bookings
      .map((b) => b.uuid)
      .join(" ")}`
  );

  // lets build prices Ids that are concerned by the synced bookings
  // and that have no timeSlotId
  const priceIds = [
    ...new Set(
      bookings
        .map((b) =>
          b.pssList.filter((pss) => !pss.timeSlotId).map((pss) => pss.priceId)
        )
        .flat()
    ),
  ];

  if (!priceIds || !priceIds.length) {
    // The booking had only tickets products, lets  not try to sync retails
    return;
  }

  // Synch not only categories of the booking but categories linked to the same stock entity as well
  const categoryIds = [
    ...new Set(
      bookings
        .map((b) =>
          b.pssList.filter((pss) => pss.timeSlotId).map((pss) => pss.categoryId)
        )
        .flat()
    ),
  ];
  // 1- get the categories with categoryIds from localDB
  const dbCategories = await getProductCategoriesByIds(
    categoryIds.map((c) => Number(c))
  );

  const pricesIdsToSynch: number[] = [];
  // 2- consider their stocksIds array
  const stockIds: number[] = [];
  dbCategories.forEach((c) => {
    if (c.stockIds && c.stockIds.length) {
      c.stockIds.forEach((s) => {
        if (s) {
          stockIds.push(Number(s));
        }
      });
    } else {
      // not a shared stock ticket, add its prices ids directly to be synced
      pricesIdsToSynch.push(...c.prices.map((p) => p.id));
    }
  });
  // 3- use stocksIds array to get a list of categories from localDB to synch
  const categoriesToSynch = await getProductCategoriesByStockIds(stockIds);

  // add shared stocks prices ids to be synced
  pricesIdsToSynch.push(
    ...categoriesToSynch.flatMap((c) => c.prices.map((p) => p.id))
  );

  const cond: IRetailSyncCondition = {
    ids: pricesIdsToSynch,
  };

  // 1- Pull concerned prices from remote db
  const updatedPrices = await getSyncedPrices(cond);

  // 3- Update the timeslots values in indexedDB if updatedPrices has elements inside
  if (!updatedPrices || !updatedPrices.length) return;

  // Lets build a typed array of the updates we need to perform in the local db
  const updates: ISyncUpdates[] = [];

  for (const pr of updatedPrices) {
    const tsFields: ISyncField[] = [];

    for (const prop in pr) {
      if (prop === "id" || prop === "categoryId" || prop === "__typename") {
        // we do not updated the id and categoryId of a timeslot
        continue;
      }
      tsFields.push({ key: prop, value: String(pr[prop]) });
    }

    const upd: ISyncUpdates = { id: String(pr?.id), fields: tsFields };
    updates.push(upd);
  }

  await updatePrices(updates);
}

/**
 * Receives an array of consumed stock (eg: consumed stock by a booking), group it by price, deduct it from local stock,
 * and update local stock in indexedDB
 * @param consumedStock : an array of pssUuid with priceIds
 */
export async function updateLocalStock(
  bookingUuid: string,
  consumedStock: {
    pssType: string;
    pssUuid: string | null;
    pssPriceId: number;
    pssTimeslotId: number | null | undefined;
    addition?: boolean;
    removal?: boolean;
  }[]
) {
  // lets split the array of pss into two arrays: one for retails and one for timeslots
  const ticketsPSS = [];
  const retailsPSS = [];
  for (let i = 0; i < consumedStock.length; i++) {
    if (consumedStock[i].pssType === PSSType[PSSType.Ticket]) {
      ticketsPSS.push(consumedStock[i]);
    }
    if (consumedStock[i].pssType === PSSType[PSSType.Retail]) {
      retailsPSS.push(consumedStock[i]);
    }
  }
  if (ticketsPSS.length) {
    try {
      LogService.info(
        `Updating timeslots local stock based on what the booking: ${bookingUuid} has consumed`,
        {
          params: {
            bookingConsumedStock: ticketsPSS,
          },
        }
      );
      await updateLocalTimeslotStock(ticketsPSS);
    } catch (err: any) {
      // log error if there is any
      LogService.info(
        `Error when updating timeslots local stock based on what the booking: ${bookingUuid} has consumed`,
        {
          params: {
            bookingConsumedStock: ticketsPSS,
            error: err,
          },
        }
      );
    }
  }
  if (retailsPSS.length) {
    try {
      LogService.info(
        `Updating retails local stock based on what the booking: ${bookingUuid} has consumed`,
        {
          params: {
            bookingConsumedStock: retailsPSS,
          },
        }
      );
      await updateLocalRetailStock(retailsPSS);
    } catch (err: any) {
      // log error if there is any
      LogService.info(
        `Error when updating retails local stock based on what the booking: ${bookingUuid} has consumed`,
        {
          params: {
            bookingConsumedStock: retailsPSS,
            error: err,
          },
        }
      );
    }
  }
}

/**
 *
 */
export async function updateLocalTimeslotStock(
  consumedStock: {
    pssType: string;
    pssUuid: string | null;
    pssPriceId: number;
    pssTimeslotId: number | null | undefined;
    addition?: boolean;
    removal?: boolean;
  }[]
) {
  // 1- lets group consumedStock or timeslotId based on pssType
  const groupedStock: {
    timeslotId: number;
    quantity: number;
  }[] = [];
  consumedStock.forEach((pss) => {
    const alreadyGroupedPSS = groupedStock.find(
      (g) => g.timeslotId === pss.pssTimeslotId
    );
    if (
      // groupedStock.findIndex((g) => g.timeslotId === pss.pssTimeslotId) !== -1
      alreadyGroupedPSS
    ) {
      // this timeslotId exists already in the grouped stock, lets just increase the quantity
      // groupedStock.find((g) => (g.quantity = g.quantity + 1));
      // alreadyGroupedPSS.quantity = alreadyGroupedPSS.quantity + 1;
      if (pss.removal) {
        // so this pss was actually cancelled
        alreadyGroupedPSS.quantity = alreadyGroupedPSS.quantity - 1;
      } else {
        // so this is a new pss confirmed
        alreadyGroupedPSS.quantity = alreadyGroupedPSS.quantity + 1;
      }
    } else {
      // groupedStock.push({ timeslotId: Number(pss.pssTimeslotId), quantity: 1 });
      // this timeslotId does not exist in the grouped stock, lets add it with quantity: 1 or -1
      // depending on whether this timeslot had a new pss created or it had a pss cancelled
      if (pss.removal) {
        // so this pss was actually cancelled
        groupedStock.push({
          timeslotId: Number(pss.pssTimeslotId),
          quantity: -1,
        });
      } else {
        // so this is a new pss confirmed
        groupedStock.push({
          timeslotId: Number(pss.pssTimeslotId),
          quantity: 1,
        });
      }
    }
  });
  // 2- lets get current stock of those timeslots from indexedDB
  const dbTimeslots = await getTimeslotsByIds(
    groupedStock
      .map((g) => g.timeslotId)
      .filter((id) => id != null && !Number.isNaN(id))
  );
  const stocks = dbTimeslots.map((t) => {
    return {
      timeslotId: t.id,
      availability: t.availability,
      booked: t.booked,
      bookable: t.bookable,
      estimatedTotalCapacity: t.estimatedTotalCapacity,
      availabilityUpdated: t.availabilityUpdated,
    };
  });
  // 3- lets deduct consumed stock from current stock
  groupedStock.forEach((g) => {
    const oldStock = stocks.find((s) => s.timeslotId === g.timeslotId);
    if (oldStock) {
      // We will update following fields of the timeslot entity in indexedDB: availability/booked/estimatedTotalCapacity
      // which express what was booked and what is left
      // We will not update the field bookable, it expresses whether the timeslot is bookable or not
      // We will not update the field availabilityUpdated in order to stay welcoming the availabilty update coming from backend

      // lets update availability
      let availability = oldStock.availability;
      availability = availability - g.quantity;
      oldStock.availability = availability;

      // lets update booked
      let booked = oldStock.booked;
      booked = booked + g.quantity;
      oldStock.booked = booked;

      // lets update estimatedTotalCapacity
      let estimatedTotalCapacity = oldStock.estimatedTotalCapacity;
      estimatedTotalCapacity = estimatedTotalCapacity - g.quantity;
      oldStock.estimatedTotalCapacity = estimatedTotalCapacity;
    }
  });
  // 4- lets prepare and insert new stock in indexedDB
  const dbUpdates: ISyncUpdates[] = [];
  stocks.forEach((s) => {
    dbUpdates.push({
      id: String(s.timeslotId),
      fields: [
        { key: "availability", value: s.availability.toString() },
        { key: "booked", value: s.booked.toString() },
        {
          key: "estimatedTotalCapacity",
          value: s.estimatedTotalCapacity.toString(),
        },
        { key: "bookable", value: s.bookable.toString() },
        { key: "availabilityUpdated", value: s.availabilityUpdated.toString() },
      ],
    });
  });

  // For Timeslots: Decerase timeslot.availability - increase timeslot.booked using
  AppModule.updatedIsLoading(true);
  await updateTimeslots(dbUpdates);
  AppModule.updatedIsLoading(false);
}

/**
 *
 * @param cashfloatUuid
 * @returns
 */
export async function updateLocalRetailStock(
  consumedStock: {
    pssType: string;
    pssUuid: string | null;
    pssPriceId: number;
    pssTimeslotId: number | null | undefined;
    addition?: boolean;
    removal?: boolean;
  }[]
) {
  // 1- lets group consumedStock by priceId based on pssType
  const groupedStock: { priceId: number; quantity: number }[] = [];
  consumedStock.forEach((pss) => {
    const alreadyGroupedPSS = groupedStock.find(
      (g) => g.priceId === pss.pssPriceId
    );
    if (
      // groupedStock.findIndex((g) => g.priceId === pss.pssPriceId) !== -1
      alreadyGroupedPSS
    ) {
      // this priceId exists already in the grouped stock, lets just increase the quantity
      // groupedStock.find((g) => (g.quantity = g.quantity + 1));
      // alreadyGroupedPSS.quantity = alreadyGroupedPSS.quantity + 1;
      if (pss.removal) {
        // so this pss was actually cancelled
        alreadyGroupedPSS.quantity = alreadyGroupedPSS.quantity - 1;
      } else {
        // so this is a new pss confirmed
        alreadyGroupedPSS.quantity = alreadyGroupedPSS.quantity + 1;
      }
    } else {
      // this priceId does not exist in the grouped stock, lets add it with quantity: 1 or -1
      // depending on whether this timeslot had a new pss created or it had a pss cancelled
      // groupedStock.push({ priceId: pss.pssPriceId, quantity: 1 });
      if (pss.removal) {
        // so this pss was actually cancelled
        groupedStock.push({ priceId: pss.pssPriceId, quantity: -1 });
      } else {
        // so this is a new pss confirmed
        groupedStock.push({ priceId: pss.pssPriceId, quantity: 1 });
      }
    }
  });
  // 2- lets get current stock of those prices from indexedDB
  const dbCategories = await getProductCategoriesByPriceIds(
    groupedStock.map((g) => g.priceId)
  );
  if (!dbCategories.length) {
    return;
  }
  const stocks = dbCategories
    .map((c) => c.prices)
    .flat()
    .map((c: { quantity: number; updatedAt?: string; id: number }) => {
      return {
        quantity: c.quantity,
        updatedAt: c.updatedAt,
        priceId: c.id,
      };
    });
  // 3- lets deduct consumed stock from current stock
  groupedStock.forEach((g) => {
    const oldStock = stocks.find((c) => c.priceId === g.priceId);
    if (oldStock) {
      // We will update following fields of the price entity in indexedDB: quantity
      // which express what is left as stock
      // We will not update the field updatedAt in order to stay welcoming the availabilty update coming from backend

      // lets update quantity
      let quantity = oldStock.quantity;
      quantity = quantity - g.quantity;
      oldStock.quantity = quantity;
    }
  });
  // 4- lets prepare and insert new stock in indexedDB
  // For Retails: Decrease price.quantity using await updatePrices([{id: pss.priceId, fields:[{key:'quantity',value:'5'}]}])
  const dbUpdates: ISyncUpdates[] = [];
  stocks.forEach((s) => {
    dbUpdates.push({
      id: String(s.priceId),
      fields: [
        { key: "quantity", value: s.quantity.toString() },
        { key: "updatedAt", value: s.updatedAt?.toString() || "" },
      ],
    });
  });
  AppModule.updatedIsLoading(true);
  await updatePrices(dbUpdates);
  AppModule.updatedIsLoading(false);
}

/**
 * Get Untyped Bookings from local db
 * @param cashfloatUuid Cashfloat Uuid
 */
export async function getUntypedBookingsByCashfloat(
  cashfloatUuid: string
): Promise<IProductSlotBooking[]> {
  const cashfloatBookings: IProductSlotBooking[] = [];

  try {
    const limit = 20;
    let skip = 0;
    let hasMore = true;
    while (hasMore) {
      const bookings = await getBookingsByCashfloatUuid(cashfloatUuid, {
        limit,
        skip,
      });

      hasMore = bookings.length === limit;
      skip += bookings.length;

      cashfloatBookings.push(...bookings);

      if (bookings.length < limit) break;
    }
  } catch (error) {
    LogService.info(
      "Error when getting untyped bookings by cashfloatUuid from Dexie",
      {
        params: { error },
      }
    );
  }
  return cashfloatBookings;
}
